import { useAutoTranslation, useUserStore } from "@/entities"
import {
  authControllerCreateGuest,
  authControllerSignIn,
  type AuthDtoReq,
} from "@/shared/api/generated"
import { LANG_KEYS } from "@/shared/constants"
import { useMutation } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useState } from "react"
import { useForm } from "react-hook-form"

export function useLoginForm() {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ email: string; password: string }>({
    defaultValues: {
      email: "",
      password: "",
    },
  })
  const userStore = useUserStore()

  const [isLoading, setIsLoading] = useState(false)

  const [formError, setFormError] = useState("")
  const { t } = useAutoTranslation()
  const formMutation = useMutation({
    mutationFn: (data: AuthDtoReq) => authControllerSignIn(data),
    async onSuccess({ access_token }) {
      setFormError("")
      await userStore.login(access_token)

      reset()
      setIsLoading(false)
    },
    onError(e: AxiosError<{ message: string }>) {
      setIsLoading(false)
      if (e.response) {
        setFormError(
          e.response.data.message ?? t(LANG_KEYS.SOMETHING_WENT_WRONG)
        )
      }
    },
  })
  const guestMutation = useMutation({
    mutationFn: () =>
      authControllerCreateGuest({
        registeredDevice: "WEBSITE",
        clientType: "WEB",
        deviceName: "Web browser",
      }),
    async onSuccess({ access_token }) {
      setFormError("")
      await userStore.login(access_token)
    },
    onError(e: AxiosError<{ message: string }>) {
      setFormError(
        e.response?.data.message ?? t(LANG_KEYS.SOMETHING_WENT_WRONG)
      )
    },
  })

  return {
    handleSubmit: handleSubmit(values => {
      setIsLoading(true)
      setFormError("")
      formMutation.mutate(values)
    }),
    errors,
    control,
    isLoading,
    continueAsGuest: () => guestMutation.mutate(),
    isGuestLoading: guestMutation.isPending,
    formError,
    setFormError,
  }
}
